import { LoadingOutlined } from '@ant-design/icons';
import { Auth, Cache } from 'aws-amplify';
import { startTokenRefreshTimer } from 'src/_shared/api/ApiProviderComponent.jsx';
import Cookies from 'js-cookie';
import get from 'lodash/get';
import { Component } from 'react';
import { withApollo } from 'react-apollo';
import {
	GetUserByCognitoId,
	updateUser,
} from 'src/_shared/api/graphql/custom/users/';
import axios from 'axios';
import { decryptUsingAES256 } from 'src/_shared/api/settings';
import { GetAccountClaimByEmployeeIdCompanyId } from 'src/graphql/custom/account-claims/';
import { slackNotificationAuth } from 'src/_shared/services/sendSlacknotificationApiService.js';

const lambda = async (body, auth = 'cognito') => {
	try {
		let response;

		if (auth === 'cognito') {
			const federatedToken = Cache.getItem('federatedToken');
			const Authorization = get(federatedToken, 'access_token')
				? federatedToken.access_token
				: (await Auth.currentSession()).getAccessToken()?.jwtToken;
			const url =
				'https://a0youn41a1.execute-api.us-east-2.amazonaws.com/default/api';
			response = axios.post(url, body, {
				headers: {
					Authorization,
					'Access-Control-Allow-Origin': '*',
				},
			});
		} else if (auth === 'apiKey') {
			const configMode = 'PROD';
			const Authorization =
				process.env[`REACT_APP_${configMode}_LAMBDADATACALLSKEY`];
			const url =
				'https://f096mzqyo7.execute-api.us-east-2.amazonaws.com/default/api';
			response = await axios.post(url, body, {
				headers: {
					Authorization,
				},
			});
		}

		return response?.data;
	} catch (error) {
		throw error;
	}
};

const btoa = (string) => {
	try {
		return window.btoa(string);
	} catch (error) {
		console.log(error);
		return '';
	}
};
const parse = (value) => {
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
};

const auth = async (code, settings) => {
	const request = {
		code,
		requestType: 'saml',
	};
	const provider = get(settings, 'provider');
	if (provider) request.provider = provider;
	const auth = await lambda(
		{
			endpoint: 'auth-prod-api',
			variables: request,
		},
		'apiKey'
	);
	return parse(decryptUsingAES256(get(auth, 'token')));
};

const graphql = async (variables, auth = 'apiKey') => {
	try {
		const configMode = 'PROD';
		variables.configMode = configMode;
		const response = await lambda(
			{
				endpoint: `graphql`,
				variables,
			},
			auth
		);
		return response;
	} catch (error) {
		console.error(error);
	}
};

const parseJwt = (token) => {
	try {
		if (!token) {
			return;
		}

		const base64Url = token.split('.')[1]
			? token.split('.')[1]
			: token.split('=')[1];
		const base64 = base64Url
			.replaceAll('-', '+')
			.replaceAll('_', '/')
			.replaceAll('%3D', '=');
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map(function (c) {
					return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join('')
		);
		return parse(jsonPayload);
	} catch {
		return null;
	}
};
class SAMLWebLoginComponent extends Component {
	constructor(props) {
		super(props);
		this.state = {
			company: get(props, 'currentUser.company'),
			backgroundImageURL: '',
		};
		this.onAuthentication = this.onAuthentication.bind(this);
	}

	async componentDidMount() {
		const { setCurrentUser } = this.props;
		let { host } = window.location;
		host = host.replace('www.', '');

		let federatedToken = await this.checkExistingTokens();
		
		if (!federatedToken) {
			federatedToken = await this.requestJwtToken();
		}

		if (!federatedToken) {
			console.error('Failed to obtain federated token');
			// Check if stale token but no redirect yet (user accessed SAML route without auth code, but has unrefreshed token set)
			const hasAuthCode = this.props.location.search.includes('code=');
			
			if (!hasAuthCode) {
				// Redirect to company-specific SSO re-authentication
				await this.initiateSSORAuthentication();
				return;
			} else {
				this.props.history.push({
					pathname: '/login',
					state: {
						err: 'Failed to authenticate with SSO provider. Please try again.',
					},
				});
				return;
			}
		}
		// Store the complete federated token (including refresh_token)
		Cache.setItem('federatedToken', federatedToken);
		// For SSO logins, we should use id_token as the JWT
		const jwtIdToken = get(federatedToken, 'id_token');
		Cookies.set('jwt', jwtIdToken);

		// Set initial expiration time for token refresh mechanism
		const expiresIn = get(federatedToken, 'expires_in', 3600);
		const expiresAt = Date.now() + expiresIn * 1000;

		// Store refresh information with proper token data
		const federatedRefreshToken = {
			token: jwtIdToken,
			expires_at: expiresAt,
			refresh_token: get(federatedToken, 'refresh_token'),
		};
		Cache.setItem('federatedRefreshToken', federatedRefreshToken);
		const redirectURL = this.props.location.hash;
		const idToken = parseJwt(get(federatedToken, 'id_token'));
		if (idToken === undefined) {
			this.setState({ visible: true });
		} else {
			const issuer = idToken.iss;
			const domain = issuer.slice(8, issuer.length);
			const provider = idToken.identities[0].providerName;
			const saml = await auth(provider, { provider: true });
			const identityPoolId = saml.identityPool;
			const user = {
				name: idToken.name,
				email: idToken.email,
			};
			Auth.configure({
				identityPoolId,
				region: 'us-east-2',
			});
			await Auth.federatedSignIn(
				domain,
				{
					token: get(federatedToken, 'id_token'),
					expires_at: idToken.exp * 1000 + Date.now(),
				},
				user
			);

			const authenticatedUser = await Auth.currentAuthenticatedUser();
			const actualToken = parseJwt(authenticatedUser.token);
			const email = get(actualToken, 'email', '');
			const emailDomain = email.split('@')[1];
			const cognitoId = actualToken?.['cognito:username'];
			if (cognitoId) {
				try {
					const { data } = await this.props.client.query({
						query: GetUserByCognitoId,
						variables: {
							cognitoId,
						},
					});
					const currentUser = data?.getUserByCognitoId;

					if (data && currentUser && !currentUser.active) {
						const error = new Error('User Disabled');
						error.code = 'UserDisabledException';
						throw error;
					} else {
						setCurrentUser(currentUser);
						await this.onAuthentication(
							federatedToken.id_token,
							currentUser,
							actualToken
						);
						let loginValue = Boolean(currentUser.lastLoginValue);
						if (currentUser.company.resetPopup) {
							loginValue = false;
						}

						window.localStorage.setItem('lastLoginValue', loginValue);
						window.localStorage.setItem('firstLoginValue', loginValue);

						const redirectURL =
							localStorage.getItem('redirectURL') ?? '/dashboard';
						localStorage.removeItem('redirectURL');
						window.location.href = redirectURL;
					}
				} catch (error) {
					const errorMessage = `java.util.ArrayList at velocity[line 2, column 31]`;
					// Create a new user if one does not exist already.
					const provider = get(actualToken, 'identities[0].providerName');
					const saml = await auth(provider, { provider: true });
					let companyId = get(saml, 'companyId');
					if (provider === 'Google') {
						const company =
							await this.getCompanyIdBySSOGoogleDomain(emailDomain);
						companyId = get(company, 'id', '');
					}

					if (!companyId) {
						const error_ =
							'The provided google account does not match a company on record. Please sign in using your company email address.';
						this.props.history.push({
							pathname: '/login',
							state: {
								err: error_,
							},
						});
					} else if (companyId && error.message.includes(errorMessage)) {
						this.props.history.push({
							pathname: `/newsamluser/${companyId}`,
							state: {
								token: actualToken,
								redirectURL,
							},
						});
					}
				}
			}
		}
	}

	onAuthentication = async (authToken, currentUser, token) => {
		// Set the JWT cookie with the reduced token
		const reducedToken = this.reduceJwt(authToken);
		Cookies.set('jwt', reducedToken);

		// Make sure we're storing the auth type for token refresh
		localStorage.setItem('authType', 'saml');

		// Initialize token refresh timer for SAML authentication
		try {
			startTokenRefreshTimer('saml');
		} catch (error) {
			console.error('Failed to initialize token refresh timer:', error);
		}

		this.setState({
			auth: true,
		});
		if (token) {
			try {
				let message = '';
				for (const [key, value] of Object.entries(token)) {
					message += `${key}: ${value} \n`;
				}

				await slackNotificationAuth({
					username: 'errors',
					title: `SAML Web Login User: ${get(currentUser, 'firstName')} ${get(
						currentUser,
						'lastName'
					)}: ${get(currentUser, 'id')}`,
					message,
				});
			} catch (error) {
				console.log(error);
				return '';
			}
		}
	};

	getAccountClaimByEmployeeCompanyId = async (employeeId, companyId) => {
		return new Promise(async (resolve, reject) => {
			try {
				const { client } = this.props;
				const { data } = await client.query({
					query: GetAccountClaimByEmployeeIdCompanyId,
					variables: { employeeId, companyId },
				});
				const claim = {
					...data.getAccountClaimByEmployeeIdCompanyId,
				};
				return resolve(claim);
			} catch {
				return resolve(null);
			}
		});
	};

	getCompanyIdBySSOGoogleDomain = async (ssoGoogleDomain) => {
		try {
			const company = await graphql({
				input: { ssoGoogleDomain },
				query: 'getCompanyBySSOGoogleDomain',
			});
			return company;
		} catch {
			return '';
		}
	};

	getMobileOperatingSystem = () => {
		const userAgent = navigator.userAgent || navigator.vendor || window.opera;
		if (/android/i.test(userAgent) && !window.MSStream) {
			const width = window.innerWidth || document.body.clientWidth;
			return width > 1000 ? 'Tablet' : 'Android';
		}

		// IOS detection from: http://stackoverflow.com/a/9039885/177710
		if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
			const width = window.innerWidth || document.body.clientWidth;
			return width > 1000 ? 'Tablet' : 'iOS';
		}
	};

	setBackgroundImage = (company) => {
		const env = getEnvironment();
		const { backgroundImageURL } = this.state;
		const whiteLabel = get(company, 'whiteLabel');
		let backgroundImage = null;
		if (env !== 'prod' && whiteLabel && backgroundImageURL) {
			const key = get(company, 'background.key', false);
			backgroundImage = `url(${backgroundImageURL})`;
			this.setState({ backgroundImage });
		} else {
			backgroundImage = `url(${LoginBackground})`;
			this.setState({ backgroundImage });
		}
	};

	reduceJwt = (token) => {
		try {
			const base64Url = token.split('.')[1];
			const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split('')
					.map(function (c) {
						return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
					})
					.join('')
			);
			const encoded = `${encodeURIComponent(btoa(jsonPayload))}`;
			return encoded;
		} catch (error) {
			console.log(error);
			return token;
		}
	};

	checkExistingTokens = async () => {
		try {
			const existingFederatedToken = Cache.getItem('federatedToken');
			const existingRefreshToken = Cache.getItem('federatedRefreshToken');

			if (existingFederatedToken && existingFederatedToken.id_token) {
				const tokenData = parseJwt(existingFederatedToken.id_token);
				const currentTime = Math.floor(Date.now() / 1000);
				const FIVE_MINUTES = 5 * 60;

				if (tokenData && tokenData.exp && (tokenData.exp - currentTime) > FIVE_MINUTES) {
					console.log('Using existing valid federated token');
					return existingFederatedToken;
				}

				if (existingRefreshToken && existingRefreshToken.refresh_token) {
					console.log('Attempting to refresh expired token');
					try {
						const refreshedToken = await this.refreshFederatedToken(existingRefreshToken.refresh_token);
						if (refreshedToken) {
							return refreshedToken;
						}
					} catch (error) {
						console.warn('Token refresh failed:', error);
						Cache.removeItem('federatedToken');
						Cache.removeItem('federatedRefreshToken');
					}
				}
			}

			return null;
		} catch (error) {
			console.error('Error checking existing tokens:', error);
			return null;
		}
	};

	refreshFederatedToken = async (refreshToken) => {
		try {
			const federatedClientId = Cache.getItem('federatedClientId');
			const federatedClientSecret = Cache.getItem('federatedClientSecret');
			const federatedDomain = Cache.getItem('federatedDomain');
			const federatedRegion = Cache.getItem('federatedRegion') || 'us-east-2';

			if (!federatedClientId || !federatedClientSecret || !federatedDomain) {
				throw new Error('Missing federated auth configuration');
			}

			const { origin } = window.location;
			const scope = 'aws.cognito.signin.user.admin+email+openid+phone+profile';
			const body = `client_id=${federatedClientId}&client_secret=${federatedClientSecret}&grant_type=refresh_token&refresh_token=${refreshToken}&scope=${scope}`;
			const url = `https://${federatedDomain}.auth.${federatedRegion}.amazoncognito.com/oauth2/token`;

			const response = await axios.post(url, body, {
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
					Authorization: 'Basic ' + btoa(federatedClientId + ':' + federatedClientSecret),
				},
			});

			const newFederatedToken = response.data;
			
			Cache.setItem('federatedToken', newFederatedToken);
			
			const expiresIn = get(newFederatedToken, 'expires_in', 3600);
			const expiresAt = Date.now() + expiresIn * 1000;
			const federatedRefreshToken = {
				token: newFederatedToken.id_token,
				expires_at: expiresAt,
				refresh_token: get(newFederatedToken, 'refresh_token', refreshToken),
			};
			Cache.setItem('federatedRefreshToken', federatedRefreshToken);

			console.log('Successfully refreshed federated token');
			return newFederatedToken;
		} catch (error) {
			console.error('Error refreshing federated token:', error);
			throw error;
		}
	};

	initiateSSORAuthentication = async () => {
		try {
			let federatedOrgCode = Cache.getItem('federatedOrgCode');
			
			if (!federatedOrgCode) {
				console.warn('No federated org code found, redirecting to login');
				this.props.history.push({
					pathname: '/login',
					state: {
						err: 'Session expired. Please sign in again.',
					},
				});
				return;
			}

			const federatedOrgData = await auth(federatedOrgCode);
			const domain = get(federatedOrgData, 'domain');
			const clientId = get(federatedOrgData, 'clientId');
			const region = get(federatedOrgData, 'region', 'us-east-2');

			if (!domain || !clientId) {
				throw new Error('Invalid federated org configuration');
			}

			Cache.setItem('federatedDomain', domain);
			Cache.setItem('federatedRegion', region);
			Cache.setItem('federatedClientId', clientId);

			const { origin } = window.location;
			const redirectUri = `${origin}/saml-auth/login`;
			const scope = 'aws.cognito.signin.user.admin+email+openid+phone+profile';
			const responseType = 'code';
			
			const ssoUrl = `https://${domain}.auth.${region}.amazoncognito.com/oauth2/authorize?client_id=${clientId}&response_type=${responseType}&scope=${scope}&redirect_uri=${encodeURIComponent(redirectUri)}`;
			
			console.log('Redirecting to SSO re-authentication');
			window.location.href = ssoUrl;
		} catch (error) {
			console.error('Error initiating SSO re-authentication:', error);
			this.props.history.push({
				pathname: '/login',
				state: {
					err: 'Unable to re-authenticate with SSO provider. Please try again.',
				},
			});
		}
	};

	async requestJwtToken() {
		try {
			const accessCode = get(this.props, 'location.search', '').split('?code=');
			const code = get(accessCode, `[1]`);
			const federatedOrgCode = Cache.getItem('federatedOrgCode');
			const federatedOrgData = await auth(federatedOrgCode);
			const clientId = get(federatedOrgData, 'clientId');
			const clientSecret = get(federatedOrgData, 'clientSecret');
			const domain = get(federatedOrgData, 'domain');
			const region = get(federatedOrgData, 'region', 'us-east-2');
			Cache.setItem('federatedDomain', domain);
			Cache.setItem('federatedRegion', region);
			Cache.setItem('federatedClientId', clientId);
			Cache.setItem('federatedClientSecret', clientSecret);
			const { origin } = window.location;
			const scope = 'aws.cognito.signin.user.admin+email+openid+phone+profile';
			const body = `client_id=${clientId}&client_secret=${clientSecret}&grant_type=authorization_code&code=${code}&scope=${scope}&redirect_uri=${origin}/saml-auth/login`;
			const url = `https://${domain}.auth.${region}.amazoncognito.com/oauth2/token`;
			const response = await axios.post(url, body, {
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
					Authorization: 'Basic ' + btoa(clientId + ':' + clientSecret),
				},
			});
			const federatedToken = response.data;
			Cache.setItem('federatedToken', federatedToken);
			return federatedToken;
		} catch (error) {
			console.log(error);
			return undefined;
		}
	}

	render() {
		return (
			<div className="custom-loader">
				<LoadingOutlined width={60} height={60} />
			</div>
		);
	}
}

export default withApollo(SAMLWebLoginComponent);
